import type { ReactNode } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { getPartnerHistory, getDisplayErrorMessage, type PartnerHistoryKind, type PartnerHistoryPage } from '@/lib/api';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import styles from './PartnerDirectory.module.css';

interface Props<T extends { id: string }> {
  agentId: string;
  kind: PartnerHistoryKind;
  title: string;
  hint?: ReactNode;
  columns: Column<T>[];
  empty: string;
  /** Payout status group, sent as ?status (payouts only). */
  status?: string;
  onRowClick?: (row: T) => void;
  /** Rendered above the table from the first page (the payouts summary). */
  header?: (first: PartnerHistoryPage<T> | undefined) => ReactNode;
}

/**
 * One history source on the partner profile: a page at a time from
 * GET /admin/agents/:id/history/:kind, newest first, with "Show more" while
 * the Api reports a next cursor. Each section loads on its own, so a slow
 * source never holds the others.
 */
export function PartnerHistorySection<T extends { id: string }>({
  agentId, kind, title, hint, columns, empty, status, onRowClick, header,
}: Props<T>) {
  const q = useInfiniteQuery({
    queryKey: ['partner-history', agentId, kind, status ?? 'all'],
    // The Api's own refusal text (for example a Super Admin only source)
    // rather than ky's status line.
    queryFn: async ({ pageParam }) => {
      try { return await getPartnerHistory<T>(agentId, kind, { cursor: pageParam, status }); }
      catch (err) { throw new Error(await getDisplayErrorMessage(err, `Could not load ${title.toLowerCase()}.`)); }
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
  const rows = q.data?.pages.flatMap((p) => p.rows) ?? [];

  return (
    <section className={styles.section} aria-label={title}>
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>{title}</h2>
      </div>
      {hint && <p className={styles.sectionHint}>{hint}</p>}
      {header?.(q.data?.pages[0])}
      {q.isLoading ? (
        <div className={styles.loading}><Loader2 size={16} className={styles.spin} /> Loading</div>
      ) : q.error ? (
        <p className={styles.error} role="alert">{q.error.message}</p>
      ) : (
        <ResponsiveTable columns={columns} rows={rows} rowKey={(r) => r.id} onRowClick={onRowClick} emptyMessage={empty} />
      )}
      {q.hasNextPage && (
        <button type="button" className={`${styles.secondaryBtn} ${styles.more}`} disabled={q.isFetchingNextPage}
          onClick={() => void q.fetchNextPage()}>
          {q.isFetchingNextPage ? <><Loader2 size={13} className={styles.spin} /> Loading</> : 'Show more'}
        </button>
      )}
    </section>
  );
}
